import {useInputControl, type FieldMetadata} from '@conform-to/react'
import {
  ChevronDownIcon,
  SelectCombobox,
  SelectComboboxItem,
  SelectComboboxList,
  SelectComboboxMatches,
  SelectComboboxPopover,
  SelectComboboxSearchInput,
  SelectComboboxTrigger,
} from '@translated/babele-react'
import clsx from 'clsx'
import React, {useEffect, useMemo, useState} from 'react'

import {translateSupportedLanguages} from '~/utils/locale'
import {getDetectedLanguageName} from './utils'

const matchSorterOptions = {
  keys: ['name'],
}

const useIsTouchDevice = () => {
  const [isTouch, setIsTouch] = useState(false)

  useEffect(() => {
    const matchMedia = window.matchMedia('(pointer: coarse)')
    setIsTouch(matchMedia.matches)

    const handler = (e: MediaQueryListEvent) => setIsTouch(e.matches)

    if ('addEventListener' in matchMedia) {
      matchMedia.addEventListener('change', handler)
    } else {
      // @ts-expect-error: Deprecated API
      matchMedia.addListener(handleChange)
    }

    return () => matchMedia.removeEventListener('change', handler)
  }, [])

  return isTouch
}

export const SelectComboboxLanguagePicker = ({
  name,
  value,
  onValueChange,
  defaultValue,
  autoDetectItem,
  ariaLabelledBy,
  detectedLanguage,
}: {
  name?: string
  value?: string
  onValueChange?: (value: string) => void
  defaultValue?: string
  autoDetectItem?: boolean
  ariaLabelledBy?: string
  detectedLanguage?: string
}) => {
  const isTouchDevice = useIsTouchDevice()

  const selectedItemName =
    value == ''
      ? detectedLanguage
        ? `${getDetectedLanguageName(detectedLanguage)} (detected)`
        : 'Auto detect'
      : getDetectedLanguageName(Array.isArray(value) ? value[0] : value)

  const items = useMemo(() => {
    if (autoDetectItem) {
      return [
        {
          code: '',
          name: ' Auto Detect',
        },
        ...translateSupportedLanguages,
      ]
    }
    return translateSupportedLanguages
  }, [autoDetectItem])

  return (
    <SelectCombobox
      value={value}
      defaultValue={defaultValue}
      onValueChange={onValueChange}
    >
      <SelectComboboxTrigger
        name={name}
        aria-labelledby={ariaLabelledBy}
        render={
          <button
            className={clsx(
              'font-medium rounded-sm px-2 py-1 text-center',
              'outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
              'flex items-center gap-2',
            )}
          >
            {selectedItemName}
            <ChevronDownIcon className="hidden md:block" />
          </button>
        }
      />
      <SelectComboboxPopover
        portal
        className="max-h-[min(var(--popover-available-height,400px),400px)]"
      >
        <SelectComboboxSearchInput autoSelect={!isTouchDevice} />
        <SelectComboboxMatches
          items={items}
          matchSorterOptions={matchSorterOptions}
        >
          {({matches, VirtualizedList}) => (
            <SelectComboboxList>
              <VirtualizedList>
                {matches.map((item) => (
                  <SelectComboboxItem key={item.name} value={item.code}>
                    {item.name}
                  </SelectComboboxItem>
                ))}
              </VirtualizedList>
            </SelectComboboxList>
          )}
        </SelectComboboxMatches>
      </SelectComboboxPopover>
    </SelectCombobox>
  )
}

export const LanguagePickerConform = ({
  meta,
  onChange,
  autoDetectItem,
  ariaLabelledBy,
  detectedLanguage,
}: {
  meta: FieldMetadata<string>
  onChange?: (value: string) => void
  autoDetectItem?: boolean
  ariaLabelledBy?: string
  detectedLanguage?: string
}) => {
  const control = useInputControl(meta)
  const {change} = control

  const handleChange = React.useCallback(
    (value: string) => {
      change(value)
      onChange?.(value)
    },
    [change, onChange],
  )

  return (
    <SelectComboboxLanguagePicker
      name={meta.name}
      value={control.value}
      defaultValue={meta.initialValue}
      onValueChange={handleChange}
      aria-invalid={meta.errors ? 'true' : undefined}
      ariaLabelledBy={ariaLabelledBy}
      autoDetectItem={autoDetectItem}
      detectedLanguage={detectedLanguage}
    />
  )
}
